/**
 * @fileoverview System Roles administration page — platform-level role grid,
 * detail slide-out, and permission viewer. Includes Organization Roles tab.
 * @module system-web/pages/SystemRolesPage
 *
 * Responsibilities:
 * - Render system/org role grids with search, filter, and template support.
 * - Display role detail panel (General, Permissions, Audit Log tabs).
 * - Render granted permissions grouped by category from static catalogs.
 *
 * Dependencies:
 * - @ui-admin/components/GridPageShell, DataGrid
 * - systemRoleService.ts for role list fetching
 * - systemAuditLogService.ts for audit log timeline
 *
 * Architecture:
 * - Local-first: roles read from Dexie via systemRoleService one-shot fetch.
 * - Permission catalogs are static constants (SYSTEM_PERMISSION_CATALOG,
 *   ORG_PERMISSION_CATALOG) that must stay in sync with backend seedData.ts.
 *
 * Notes:
 * - SYSTEM_PERMISSION_CATALOG keys match seedSystemRoles() permissions arrays
 *   in api/system-api/src/db/seedData.ts (system.* prefix).
 * - ORG_PERMISSION_CATALOG keys match seedOrganizationRoles() permissions arrays.
 * - Backend seedPermissionDefinitions() is a separate, incomplete catalog that
 *   omits some keys granted by role seeds; these catalogs follow the role seeds.
 * - Super Admin wildcard bypasses catalog matching and shows all categories.
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Shield, ShieldCheck, ChevronDown, ChevronRight, Check, Eye,
} from 'lucide-react';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn, DataGridFilter, DataGridRowAction, GridTemplate } from '@ui-admin/components/DataGrid';
import TabPanel from '../components/ui/TabPanel';
import StatusBadge from '../components/ui/StatusBadge';
import AuditLogTimeline from '@ui-admin/components/AuditLogTimeline';
import { fetchAuditLogs } from '../services/systemAuditLogService';
import {
  listSystemRoles,
  listOrganizationRoles,
  type SystemRole,
  type OrganizationRole,
} from '../services/systemRoleService';

// ============================================================
// Permission catalog types
// ============================================================

interface PermEntry { key: string; label: string; description: string; }
interface PermCat { id: string; label: string; perms: PermEntry[]; }

// ---------------------------------------------------------------------------
// Static permission catalogs aligned with backend role seed data.
// Keys match the permissions arrays in seedSystemRoles() and
// seedOrganizationRoles() in api/system-api/src/db/seedData.ts.
// Note: backend seedPermissionDefinitions() is a separate, incomplete catalog
// that omits some keys granted by role seeds (e.g. org.templates.*,
// org.settings.*, org.users.delete). These catalogs follow the role seed
// data — the authoritative source of what each role actually grants.
// ---------------------------------------------------------------------------
const SYSTEM_PERMISSION_CATALOG: PermCat[] = [
  {
    id: 'tenants',
    label: 'Tenants',
    perms: [
      { key: 'system.tenants.access', label: 'Access Tenants', description: 'Can see tenant management' },
      { key: 'system.tenants.view',   label: 'View Tenants',   description: 'Read tenant records and configuration' },
      { key: 'system.tenants.manage', label: 'Manage Tenants', description: 'Can create/edit tenants' },
      { key: 'system.tenants.edit',   label: 'Edit Tenants',   description: 'Modify tenant settings and billing' },
      { key: 'system.tenants.create', label: 'Create Tenants', description: 'Start new tenant onboarding drafts' },
      { key: 'system.tenants.trial.extend',  label: 'Extend Trial',  description: 'Can extend trial periods' },
      { key: 'system.tenants.trial.convert', label: 'Convert Trial', description: 'Can convert trials to paid' },
    ],
  },
  {
    id: 'users',
    label: 'Users',
    perms: [
      { key: 'system.users.access', label: 'Access Users', description: 'Can see user management' },
      { key: 'system.users.view',   label: 'View Users',   description: 'Read system user records' },
      { key: 'system.users.manage', label: 'Manage Users', description: 'Can create/edit users' },
      { key: 'system.users.edit',   label: 'Edit Users',   description: 'Modify system user profiles and roles' },
      { key: 'system.users.create', label: 'Create Users', description: 'Invite new system-level users' },
    ],
  },
  {
    id: 'organizations',
    label: 'Organizations',
    perms: [
      { key: 'system.organizations.access', label: 'Access Organizations', description: 'Can see org management' },
      { key: 'system.organizations.view',   label: 'View Organizations',   description: 'Read organization records' },
    ],
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    perms: [
      { key: 'system.onboarding.access', label: 'Access Onboarding', description: 'Can see onboarding section' },
      { key: 'system.onboarding.view',   label: 'View Onboarding',   description: 'Read onboarding drafts and wizard state' },
      { key: 'system.onboarding.edit',  label: 'Edit Onboarding',   description: 'Modify and progress onboarding drafts' },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    perms: [
      { key: 'system.billing.access', label: 'Access Billing', description: 'Can see billing section' },
      { key: 'system.billing.view',   label: 'View Billing',   description: 'Can view billing details' },
      { key: 'system.billing.edit',   label: 'Edit Billing',   description: 'Can modify billing' },
      { key: 'system.usage.view',     label: 'View Usage',     description: 'Can view usage metrics' },
    ],
  },
  {
    id: 'platform',
    label: 'Platform Config',
    perms: [
      { key: 'tiers.view',         label: 'View Tiers',         description: 'Read tier definitions and limits' },
      { key: 'feature_flags.view', label: 'View Feature Flags', description: 'Read feature flag states' },
      { key: 'modules.view',       label: 'View Modules',       description: 'Read module definitions' },
      { key: 'lookups.view',       label: 'View Lookups',       description: 'Read system lookup values' },
    ],
  },
  {
    id: 'audit',
    label: 'Audit',
    perms: [
      { key: 'audit_logs.view', label: 'View Audit Logs', description: 'Read platform audit log entries' },
    ],
  },
];

const ORG_PERMISSION_CATALOG: PermCat[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    perms: [
      { key: 'org.dashboard.access', label: 'Access Dashboard', description: 'Can see org dashboard' },
    ],
  },
  {
    id: 'departments',
    label: 'Departments',
    perms: [
      { key: 'org.departments.access', label: 'Access Departments', description: 'Can see departments list' },
      { key: 'org.departments.view',   label: 'View Departments',   description: 'Can view department details' },
      { key: 'org.departments.create', label: 'Create Departments', description: 'Can create new departments' },
      { key: 'org.departments.edit',   label: 'Edit Departments',   description: 'Can modify departments' },
    ],
  },
  {
    id: 'org-users',
    label: 'Users',
    perms: [
      { key: 'org.users.access', label: 'Access Users', description: 'Can see org users' },
      { key: 'org.users.view',   label: 'View Users',   description: 'Can view org user list' },
      { key: 'org.users.create', label: 'Invite Users', description: 'Can invite org users' },
      { key: 'org.users.edit',   label: 'Edit Users',   description: 'Can modify org user roles' },
      { key: 'org.users.delete',  label: 'Remove Users',  description: 'Can remove users from organization' },
    ],
  },
  {
    id: 'templates',
    label: 'Templates',
    perms: [
      { key: 'org.templates.access', label: 'Access Templates', description: 'Can see templates section' },
      { key: 'org.templates.view',   label: 'View Templates',   description: 'Can view templates' },
      { key: 'org.templates.create', label: 'Create Templates', description: 'Can create templates' },
      { key: 'org.templates.edit',   label: 'Edit Templates',   description: 'Can modify templates' },
      { key: 'org.templates.share',  label: 'Share Templates',  description: 'Can share templates' },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    perms: [
      { key: 'org.analytics.access', label: 'Access Analytics', description: 'Can see analytics' },
      { key: 'org.analytics.view',   label: 'View Analytics',   description: 'Can view reports' },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    perms: [
      { key: 'org.settings.access', label: 'Access Settings', description: 'Can see org settings' },
      { key: 'org.settings.edit',   label: 'Edit Settings',   description: 'Can modify organization-level settings' },
    ],
  },
];

// ============================================================
// Helpers
// ============================================================

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusVariant(s: string): 'success' | 'warning' | 'neutral' {
  if (s === 'active') return 'success';
  if (s === 'disabled') return 'warning';
  return 'neutral';
}

function permissionCount(role: SystemRole | OrganizationRole): string {
  if (role.permissions.includes('*')) return 'All';
  return String(role.permissions.length);
}

// ============================================================
// Grid Columns
// ============================================================

type AnyRole = SystemRole | OrganizationRole;

const COLUMNS: DataGridColumn<AnyRole>[] = [
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    width: 200,
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="flex items-center gap-2">
          {row.permissions.includes('*')
            ? <ShieldCheck size={14} className="text-orange-500 shrink-0" />
            : <Shield size={14} className="text-gray-400 shrink-0" />}
          <span className="font-semibold text-gray-900 truncate">{info.getValue() as string}</span>
          {row.isBuiltIn && (
            <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 shrink-0">
              Built-in
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: 'description',
    header: 'Description',
    accessorKey: 'description',
    width: 300,
    cell: (info) => (
      <span className="text-gray-600 truncate">{(info.getValue() as string) || '—'}</span>
    ),
  },
  {
    id: 'type',
    header: 'Type',
    accessorFn: () => 'Built-in',
    width: 100,
    cell: () => <StatusBadge label="Built-in" variant="info" />,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    width: 100,
    cell: (info) => {
      const v = (info.getValue() as string) || 'active';
      return (
        <StatusBadge
          label={v.charAt(0).toUpperCase() + v.slice(1)}
          variant={statusVariant(v)}
        />
      );
    },
  },
  {
    id: 'permissions',
    header: 'Permissions',
    accessorFn: (row) => permissionCount(row),
    width: 110,
    cell: (info) => {
      const v = info.getValue() as string;
      return v === 'All'
        ? <span className="text-orange-600 font-medium text-sm">All</span>
        : <span className="text-gray-700 text-sm">{v}</span>;
    },
  },
  {
    id: 'updatedAt',
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    width: 140,
    cell: (info) => (
      <span className="text-gray-500 text-xs">{formatDate(info.getValue() as string)}</span>
    ),
  },
];

// ============================================================
// Filters
// ============================================================

const FILTERS: DataGridFilter[] = [
  {
    id: 'status',
    label: 'Status',
    type: 'multi-select',
    options: [
      { value: 'active',   label: 'Active'   },
      { value: 'disabled', label: 'Disabled' },
    ],
  },
];

// ============================================================
// Seed Templates
// ============================================================

const SYSTEM_SEED_TEMPLATES: GridTemplate[] = [
  {
    _id: 'tpl-sysroles-all',
    gridType: 'system-roles',
    name: 'All System Roles',
    description: 'All platform-level roles',
    state: {
      columnVisibility: {}, columnOrder: [], sorting: [{ id: 'name', desc: false }],
      filterValues: {}, pageSize: 25, searchText: '',
    },
    scope: 'system',
    isDefault: true,
    isPinned: false,
    usageCount: 0,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
    createdBy: { userId: 'system', displayName: 'System' },
    updatedBy: { userId: 'system', displayName: 'System' },
  },
];

const ORG_SEED_TEMPLATES: GridTemplate[] = [
  {
    _id: 'tpl-orgroles-all',
    gridType: 'org-roles',
    name: 'All Organization Roles',
    description: 'All organization-level roles',
    state: {
      columnVisibility: {}, columnOrder: [], sorting: [{ id: 'name', desc: false }],
      filterValues: {}, pageSize: 25, searchText: '',
    },
    scope: 'system',
    isDefault: true,
    isPinned: false,
    usageCount: 0,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
    createdBy: { userId: 'system', displayName: 'System' },
    updatedBy: { userId: 'system', displayName: 'System' },
  },
];

// ============================================================
// Permission Viewer (read-only)
// ============================================================

interface SystemPermissionViewerProps {
  permissions: string[];
  isWildcard: boolean;
  catalog: PermCat[];
}

function SystemPermissionViewer({ permissions, isWildcard, catalog }: SystemPermissionViewerProps) {
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(catalog.map((c) => c.id)),
  );
  const permSet = useMemo(() => new Set(permissions), [permissions]);

  const toggleCat = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const visibleCats = catalog.filter((cat) =>
    isWildcard || cat.perms.some((p) => permSet.has(p.key)),
  );

  return (
    <div className="space-y-1">
      {isWildcard && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 mb-3">
          <ShieldCheck size={16} className="text-orange-600 shrink-0" />
          <span className="text-xs text-orange-800">
            This role has wildcard access — all current and future permissions are granted.
          </span>
        </div>
      )}
      {!isWildcard && permissions.length === 0 && (
        <p className="text-xs text-gray-400 px-2 py-4 text-center">No permissions assigned.</p>
      )}
      {(isWildcard ? catalog : visibleCats).map((cat) => {
        const isExpanded = expanded.has(cat.id);
        const grantedCount = isWildcard
          ? cat.perms.length
          : cat.perms.filter((p) => permSet.has(p.key)).length;
        if (!isWildcard && grantedCount === 0) return null;

        return (
          <div key={cat.id} className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleCat(cat.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors rounded-lg"
            >
              {isExpanded
                ? <ChevronDown size={14} className="text-gray-400 shrink-0" />
                : <ChevronRight size={14} className="text-gray-400 shrink-0" />}
              <span className="text-sm font-medium text-gray-900 flex-1">{cat.label}</span>
              <span className="text-[10px] text-gray-400">{grantedCount}/{cat.perms.length}</span>
            </button>
            {isExpanded && (
              <div className="px-3 pb-3 space-y-1">
                {cat.perms.map((perm) => {
                  const granted = isWildcard || permSet.has(perm.key);
                  return (
                    <div
                      key={perm.key}
                      className={`flex items-start gap-2.5 px-2 py-1.5 rounded text-xs ${
                        granted ? '' : 'opacity-40'
                      }`}
                    >
                      <div className="mt-0.5 shrink-0">
                        {granted
                          ? <Check size={13} className="text-green-600" />
                          : <div className="h-3.5 w-3.5 rounded border border-gray-300" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800">{perm.label}</div>
                        <div className="text-gray-500 text-[10px]">{perm.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// Role Detail Panel
// ============================================================

interface RoleDetailPanelProps {
  role: AnyRole;
  roleType: 'system' | 'org';
  permissionCatalog: PermCat[];
}

function RoleDetailPanel({ role, roleType, permissionCatalog }: RoleDetailPanelProps) {
  const isWildcard = role.permissions.includes('*');
  const permCount = isWildcard ? 'All' : String(role.permissions.length);

  const tabs = [
    {
      id: 'general',
      label: 'General',
      content: (
        <div className="px-4 py-4 space-y-3">
          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-0.5">Name</p>
              <p className="text-sm font-semibold text-gray-900">{role.name}</p>
            </div>
            {role.description && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-0.5">Description</p>
                <p className="text-sm text-gray-700">{role.description}</p>
              </div>
            )}
          </div>
          <div className="pt-2 border-t border-gray-100 space-y-2">
            <div className="flex items-center gap-2 py-1">
              <span className="text-xs font-medium text-gray-500 w-28 shrink-0">Code</span>
              <code className="text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded font-mono">
                {role.code}
              </code>
            </div>
            <div className="flex items-center gap-2 py-1">
              <span className="text-xs font-medium text-gray-500 w-28 shrink-0">Type</span>
              <StatusBadge label="Built-in" variant="info" />
            </div>
            <div className="flex items-center gap-2 py-1">
              <span className="text-xs font-medium text-gray-500 w-28 shrink-0">Status</span>
              <StatusBadge
                label={(role.status || 'active').charAt(0).toUpperCase() + (role.status || 'active').slice(1)}
                variant={statusVariant(role.status || 'active')}
              />
            </div>
            <div className="flex items-center gap-2 py-1">
              <span className="text-xs font-medium text-gray-500 w-28 shrink-0">Permissions</span>
              <span className={`text-sm font-medium ${isWildcard ? 'text-orange-600' : 'text-gray-700'}`}>
                {isWildcard ? `All (wildcard)` : `${permCount} granted`}
              </span>
            </div>
            <div className="flex items-center gap-2 py-1">
              <span className="text-xs font-medium text-gray-500 w-28 shrink-0">Last Updated</span>
              <span className="text-xs text-gray-600">{formatDate(role.updatedAt)}</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'permissions',
      label: `Permissions (${permCount})`,
      content: (
        <div className="px-4 py-4">
          <SystemPermissionViewer
            permissions={role.permissions}
            isWildcard={isWildcard}
            catalog={permissionCatalog}
          />
        </div>
      ),
    },
    {
      id: 'audit',
      label: 'Audit Log',
      content: (
        <div className="px-2 py-2">
          <AuditLogTimeline
            documentId={role._id}
            collection={roleType === 'system' ? 'systemRoles' : 'organizationRoles'}
            theme={{ cardBg: '', cardBorder: '', accent: '', accentText: '', textPrimary: '', textSecondary: '', expandedBg: '' } as any}
            active
            fetchFn={fetchAuditLogs}
          />
        </div>
      ),
    },
  ];

  return <TabPanel tabs={tabs} />;
}

// ============================================================
// Main Page Component
// ============================================================

type ActiveTab = 'system' | 'org';

/**
 * @component SystemRolesPage
 * Purpose: Route-level admin page for viewing system/org roles.
 * Props: None — route component; receives no external props.
 * Behavior: Loads roles via Dexie service on mount, renders grid + slide-out.
 * Data Source: local (Dexie system_roles / organization_roles tables via
 *   systemRoleService.ts one-shot fetch).
 * Constraints: No direct API calls in component; data flows through service.
 * Performance: Grid paginated by GridPageShell; no client-side virtualization.
 *
 * @localFirst
 * Source of Truth: local Dexie (systemDb) for roles; server is SoR for audit logs.
 * Write Flow: read-only page — no user mutations on this surface.
 * Sync Strategy: roles pulled via standard system sync; audit logs fetched on
 *   demand via fetchFn prop to AuditLogTimeline.
 * Conflict Resolution: N/A — read-only page.
 */
export default function SystemRolesPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('system');
  const [systemRoles, setSystemRoles] = useState<SystemRole[]>([]);
  const [orgRoles, setOrgRoles] = useState<OrganizationRole[]>([]);
  const [selectedRole, setSelectedRole] = useState<AnyRole | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listSystemRoles(), listOrganizationRoles()])
      .then(([sys, org]) => {
        if (!cancelled) { setSystemRoles(sys); setOrgRoles(org); }
      })
      .catch((err) => {
        console.warn('[SystemRolesPage] Failed to load roles:', err);
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const rowActions = useMemo<DataGridRowAction<AnyRole>[]>(() => [
    { id: 'view', label: 'View', icon: Eye, onClick: (r) => setSelectedRole(r) },
  ], []);

  const currentData = activeTab === 'system' ? systemRoles : orgRoles;
  const currentGridId = activeTab === 'system' ? 'system-roles' : 'org-roles';
  const currentTemplates = activeTab === 'system' ? SYSTEM_SEED_TEMPLATES : ORG_SEED_TEMPLATES;

  const panelContent = selectedRole ? (
    <RoleDetailPanel
      role={selectedRole}
      roleType={activeTab}
      permissionCatalog={activeTab === 'system' ? SYSTEM_PERMISSION_CATALOG : ORG_PERMISSION_CATALOG}
    />
  ) : undefined;

  return (
    <div className="flex flex-col h-full">
      {/* Page-level tab switcher */}
      <div className="shrink-0 flex items-center gap-1 px-6 pt-5 pb-0 bg-white border-b border-gray-200">
        <button
          onClick={() => { setActiveTab('system'); setSelectedRole(null); }}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
            activeTab === 'system'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          System Roles
          <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-gray-100 text-gray-500">
            {systemRoles.length}
          </span>
        </button>
        <button
          onClick={() => { setActiveTab('org'); setSelectedRole(null); }}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
            activeTab === 'org'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Organization Roles
          <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-gray-100 text-gray-500">
            {orgRoles.length}
          </span>
        </button>
      </div>

      {/* Grid */}
      <div className="flex-1 min-h-0">
        <GridPageShell<AnyRole>
          key={currentGridId}
          gridId={currentGridId}
          gridType={currentGridId}
          title={activeTab === 'system' ? 'System Roles' : 'Organization Roles'}
          subtitle={
            activeTab === 'system'
              ? 'Platform-level roles assigned to system administrators'
              : 'Organization-level roles for cross-department access'
          }
          columns={COLUMNS}
          data={currentData}
          getRowId={(r) => r._id}
          rowActions={rowActions}
          filters={FILTERS}
          onRowClick={(r) => setSelectedRole(r)}
          activeRowId={selectedRole?._id ?? null}
          searchPlaceholder="Search roles…"
          panelContent={panelContent}
          panelTitle={selectedRole?.name}
          panelSubtitle={selectedRole?.description}
          onPanelClose={() => setSelectedRole(null)}
          seedTemplates={currentTemplates}
          onRefresh={reload}
          emptyTitle="No roles found"
          emptySubtitle="Roles are seeded automatically at platform startup"
        />
      </div>
    </div>
  );
}
